import {
  getRecursos as dadosRecursos,
  getIntencaoRecurso as dadosIntencaoRecurso,
  getElegibilidadeRazoes as dadosElegibilidadeRazoes,
  getPrazosRecursais as dadosPrazosRecursais,
  definirPrazosRecursais,
  ajustarPrazosRecursais,
  encerrarPrazoRecursal as encerrarPrazoRecursalApi,
  getPrazosManifestacaoIntencao as dadosPrazosManifestacaoIntencao,
  definirPrazosManifestacaoIntencao,
  encerrarPrazoManifestacaoIntencao as encerrarPrazoManifestacaoIntencaoApi,
  criarRecurso,
  criarContrarrazao,
  atualizarContrarrazaoRecurso,
  manifestarIntencaoRecurso,
  atualizarRecurso,
  atualizarRazoesRecurso,
  excluirRazoesRecurso,
  excluirContrarrazaoRecurso,
  julgamentoAutoridadeRecurso,
} from '@/resources/ofertasCompra';
import { rejectManifestacaoRequest, findManifestacaoIndex } from '@/utils/manifestacaoError';
import { SituacaoEnum } from '@/utils/enumerados';
import { identificarJanelaRecursalVigente, isPrazoRecursalVencido } from '@/utils/businessDays';

const APPEAL_INTENTION_SITUATIONS_PADRAO = new Set([
  SituacaoEnum.HABILITACAO,
  SituacaoEnum.JULGAMENTO,
]);

const APPEAL_INTENTION_SITUATIONS_CONSOLIDADA = new Set([
  SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO,
]);

const isConsolidada = auction => Boolean(auction?.manifestacao_intencao_consolidada);

const situacoesManifestacaoParaAuction = auction =>
  isConsolidada(auction)
    ? APPEAL_INTENTION_SITUATIONS_CONSOLIDADA
    : APPEAL_INTENTION_SITUATIONS_PADRAO;

const types = {
  RECURSOS: 'RECURSOS',
  INTENCAO_RECURSO: 'INTENCAO_RECURSO',
  ELEGIBILIDADE_RAZOES: 'ELEGIBILIDADE_RAZOES',
  UPSERT_RECURSO: 'UPSERT_RECURSO',
  REMOVE_RECURSO: 'REMOVE_RECURSO',
  PRAZOS_RECURSAIS: 'PRAZOS_RECURSAIS',
  PRAZO_MANIFESTACAO_INTENCAO: 'PRAZO_MANIFESTACAO_INTENCAO',
};

const getResponseData = response => response?.data?.data || response?.data || null;

const normalizeManifestacao = manifestacao => ({
  ...manifestacao,
  anexos: Array.isArray(manifestacao.anexos) ? manifestacao.anexos : [],
  criado: manifestacao.criado ? new Date(manifestacao.criado) : null,
  atualizado: manifestacao.atualizado ? new Date(manifestacao.atualizado) : null,
});

const ordenarPorCriado = (a, b) => {
  const tempoA = a.criado ? a.criado.getTime() : 0;
  const tempoB = b.criado ? b.criado.getTime() : 0;
  return tempoA - tempoB;
};

const normalizeRecurso = recurso => {
  const manifestacoes = Array.isArray(recurso.manifestacoes)
    ? recurso.manifestacoes.map(normalizeManifestacao).sort(ordenarPorCriado)
    : [];

  return {
    ...recurso,
    criado: recurso.criado ? new Date(recurso.criado) : null,
    empresasContrarrazaoRegistradas: Array.isArray(recurso.empresasContrarrazaoRegistradas)
      ? recurso.empresasContrarrazaoRegistradas.map(Number)
      : [],
    manifestacoes,
  };
};

const normalizeElegibilidadeRazoes = payload => {
  if (!payload) {
    return {
      podeApresentarRazoes: false,
      recursoJaEnviado: false,
      recursoExcluido: false,
      intencoes: [],
    };
  }

  const intencoes = Array.isArray(payload.intencoes) ? payload.intencoes : [];

  return {
    podeApresentarRazoes: Boolean(
      Object.prototype.hasOwnProperty.call(payload, 'podeApresentarRazoes')
        ? payload.podeApresentarRazoes
        : intencoes.length > 0,
    ),
    recursoJaEnviado: Boolean(payload.recursoJaEnviado),
    recursoExcluido: Boolean(payload.recursoExcluido),
    intencoes,
  };
};

const normalizeIntencaoRecurso = payload => {
  if (!payload) {
    return {
      manifestou: false,
      intencao: null,
    };
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'manifestou')) {
    return {
      manifestou: Boolean(payload.manifestou),
      intencao: payload.intencao || null,
    };
  }

  return {
    manifestou: true,
    intencao: payload,
  };
};

const parseDate = valor => {
  if (!valor) return null;
  if (valor instanceof Date) return new Date(valor.getTime());
  return new Date(valor);
};

const normalizePrazoManifestacaoIntencao = payload => {
  if (!payload) {
    return {
      definidos: false,
      dataLimite: null,
      dataDe: null,
      vigente: false,
      tempoRestanteSegundos: null,
      limites: null,
    };
  }

  const raw = payload.data && typeof payload.data === 'object' ? payload.data : payload;

  // API returns definido/dataAte; keep definidos/dataLimite aliases for UI.
  const dataLimite = parseDate(raw.dataLimite || raw.dataAte);
  let vigente = false;
  if (Object.prototype.hasOwnProperty.call(raw, 'vigente')) {
    vigente = Boolean(raw.vigente);
  } else if (dataLimite) {
    vigente = !isPrazoRecursalVencido(dataLimite);
  }

  return {
    definidos: Boolean(raw.definidos ?? raw.definido ?? Boolean(dataLimite)),
    dataLimite,
    dataDe: parseDate(raw.dataDe),
    vigente,
    tempoRestanteSegundos:
      raw.tempoRestanteSegundos != null ? Number(raw.tempoRestanteSegundos) : null,
    limites: raw.limites
      ? {
          minMinutos: Number(raw.limites.minMinutos),
          maxMinutos: Number(raw.limites.maxMinutos),
        }
      : null,
  };
};

const normalizePrazosRecursais = payload => {
  if (!payload) {
    return {
      definidos: false,
      linhaDoTempo: null,
      dataLimiteRazoes: null,
      dataLimiteContrarrazoes: null,
      dataLimiteJulgamento: null,
      regrasAjuste: null,
    };
  }

  return {
    definidos: Boolean(payload.definidos),
    linhaDoTempo: payload.linhaDoTempo
      ? {
          d0: parseDate(payload.linhaDoTempo.d0),
          razoesAte: parseDate(payload.linhaDoTempo.razoesAte),
          contrarrazoesAte: parseDate(payload.linhaDoTempo.contrarrazoesAte),
          consideracaoAte: parseDate(payload.linhaDoTempo.consideracaoAte),
        }
      : null,
    dataLimiteRazoes: parseDate(payload.dataLimiteRazoes),
    dataLimiteContrarrazoes: parseDate(payload.dataLimiteContrarrazoes),
    dataLimiteJulgamento: parseDate(payload.dataLimiteJulgamento),
    regrasAjuste: payload.regrasAjuste ?? null,
  };
};

const mutations = {
  [types.RECURSOS](state, recursos) {
    if (recursos && Array.isArray(recursos)) {
      state.recursos = recursos.map(normalizeRecurso);
    }
  },
  [types.INTENCAO_RECURSO](state, intencaoRecurso) {
    state.intencaoRecurso = normalizeIntencaoRecurso(intencaoRecurso);
  },
  [types.ELEGIBILIDADE_RAZOES](state, elegibilidadeRazoes) {
    state.elegibilidadeRazoes = normalizeElegibilidadeRazoes(elegibilidadeRazoes);
  },
  [types.UPSERT_RECURSO](state, recurso) {
    if (!recurso || typeof recurso !== 'object') return;

    const normalized = normalizeRecurso(recurso);
    const indice = findManifestacaoIndex(state.recursos, normalized);

    if (indice > -1) {
      state.recursos.splice(indice, 1, normalized);
    } else {
      state.recursos.unshift(normalized);
    }
  },
  [types.REMOVE_RECURSO](state, recursoId) {
    const indice = state.recursos.findIndex(recurso => Number(recurso.id) === Number(recursoId));

    if (indice > -1) {
      state.recursos.splice(indice, 1);
    }
  },
  [types.PRAZOS_RECURSAIS](state, prazosRecursais) {
    state.prazosRecursais = normalizePrazosRecursais(prazosRecursais);
  },
  [types.PRAZO_MANIFESTACAO_INTENCAO](state, prazoManifestacaoIntencao) {
    state.prazoManifestacaoIntencao = normalizePrazoManifestacaoIntencao(prazoManifestacaoIntencao);
  },
};

const appendEmpresaId = (dados, empresaId) => {
  if (!empresaId) return;

  if (dados instanceof FormData) {
    dados.append('empresaId', empresaId);
  } else {
    dados.empresaId = empresaId; // eslint-disable-line no-param-reassign
  }
};

const actions = {
  async buscarOfertaCompraRecursos({ commit }, ofertaCompraId) {
    const recursos = await dadosRecursos(ofertaCompraId);
    commit(types.RECURSOS, recursos.data.data);
  },
  async salvarOfertaCompraRecurso({ commit, dispatch, state, rootState }, dados) {
    const empresa = rootState.users.company;
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    appendEmpresaId(dados, empresa.id);
    const response = await criarRecurso(ofertaCompraId, dados);
    if (response.status !== 201) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    await dispatch('buscarElegibilidadeRazoes', ofertaCompraId);
    if (state.prazosRecursais?.definidos) {
      await dispatch('buscarPrazosRecursais', ofertaCompraId);
    }
    return getResponseData(response);
  },
  async editarOfertaCompraRecurso({ commit, rootState }, { id, dados }) {
    const empresa = rootState.users.company;
    appendEmpresaId(dados, empresa.id);
    const response = await atualizarRazoesRecurso(
      rootState.ofertasCompra.ofertaCompra.id,
      id,
      dados,
    );
    if (response.status !== 200) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    return getResponseData(response);
  },
  async salvarContrarrazao({ commit, rootState }, { recursoId, dados }) {
    const empresa = rootState.users.company;
    appendEmpresaId(dados, empresa.id);
    const response = await criarContrarrazao(
      rootState.ofertasCompra.ofertaCompra.id,
      recursoId,
      dados,
    );
    if (response.status !== 201) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    return getResponseData(response);
  },
  async editarContrarrazaoRecurso({ commit, rootState }, { recursoId, dados }) {
    const empresa = rootState.users.company;
    appendEmpresaId(dados, empresa.id);
    const response = await atualizarContrarrazaoRecurso(
      rootState.ofertasCompra.ofertaCompra.id,
      recursoId,
      dados,
    );
    if (response.status !== 200) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    return getResponseData(response);
  },
  async excluirRazoesRecurso({ commit, dispatch, rootState }, { recursoId, justificativa }) {
    const response = await excluirRazoesRecurso(
      rootState.ofertasCompra.ofertaCompra.id,
      recursoId,
      { justificativa },
    );
    if (response.status !== 200) return rejectManifestacaoRequest(response);
    commit(types.REMOVE_RECURSO, recursoId);
    await dispatch('buscarElegibilidadeRazoes', rootState.ofertasCompra.ofertaCompra.id);
    return getResponseData(response);
  },
  async excluirContrarrazaoRecurso({ commit, rootState }, { recursoId, justificativa }) {
    const response = await excluirContrarrazaoRecurso(
      rootState.ofertasCompra.ofertaCompra.id,
      recursoId,
      { justificativa },
    );
    if (response.status !== 200) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    return getResponseData(response);
  },
  async buscarElegibilidadeRazoes({ commit }, ofertaCompraId) {
    try {
      const response = await dadosElegibilidadeRazoes(ofertaCompraId);
      commit(types.ELEGIBILIDADE_RAZOES, getResponseData(response));
    } catch (error) {
      if (error.response?.status === 404) {
        commit(types.ELEGIBILIDADE_RAZOES, null);
        return;
      }
      throw error;
    }
  },
  async buscarOfertaCompraIntencaoRecurso({ commit }, ofertaCompraId) {
    try {
      const response = await dadosIntencaoRecurso(ofertaCompraId);
      commit(types.INTENCAO_RECURSO, getResponseData(response));
    } catch (error) {
      if (error.response?.status === 404) {
        commit(types.INTENCAO_RECURSO, null);
        return;
      }
      throw error;
    }
  },
  async sincronizarIntencaoRecursoPorSituacao(
    { commit, dispatch, rootState },
    { situacaoAnterior, situacaoAtual, ofertaCompraId } = {},
  ) {
    const auctionId = ofertaCompraId || rootState.ofertasCompra.ofertaCompra?.id;
    if (!auctionId) return;

    const auction = rootState.ofertasCompra.ofertaCompra;
    const situacoesRelevantes = situacoesManifestacaoParaAuction(auction);

    const situacao = situacaoAtual ?? auction?.situacao?.id;
    if (!situacoesRelevantes.has(situacao)) {
      return;
    }

    if (
      situacaoAnterior !== undefined &&
      situacoesRelevantes.has(situacaoAnterior) &&
      situacaoAnterior !== situacao
    ) {
      commit(types.INTENCAO_RECURSO, null);
    }

    await dispatch('buscarOfertaCompraIntencaoRecurso', auctionId);
  },
  async manifestarOfertaCompraIntencaoRecurso({ commit, rootState }) {
    const response = await manifestarIntencaoRecurso(rootState.ofertasCompra.ofertaCompra.id);
    if (response.status !== 201) return rejectManifestacaoRequest(response);
    const intencao = getResponseData(response);
    commit(types.INTENCAO_RECURSO, { manifestou: true, intencao });
    return intencao;
  },
  async responderOfertaCompraRecurso({ commit, rootState }, { id, dados }) {
    const response = await atualizarRecurso(rootState.ofertasCompra.ofertaCompra.id, id, dados);
    if (response.status !== 200) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    return getResponseData(response);
  },
  async julgarRecursoAutoridade({ commit, rootState }, { id, dados }) {
    const response = await julgamentoAutoridadeRecurso(
      rootState.ofertasCompra.ofertaCompra.id,
      id,
      dados,
    );
    if (response.status !== 200) return rejectManifestacaoRequest(response);
    commit(types.UPSERT_RECURSO, getResponseData(response));
    return getResponseData(response);
  },
  async buscarPrazosRecursais({ commit }, ofertaCompraId) {
    const response = await dadosPrazosRecursais(ofertaCompraId);
    commit(types.PRAZOS_RECURSAIS, getResponseData(response));
    return getResponseData(response);
  },
  async salvarPrazosRecursais({ commit, state, rootState }, dados) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const definidos = state.prazosRecursais?.definidos;
    const response = definidos
      ? await ajustarPrazosRecursais(ofertaCompraId, dados)
      : await definirPrazosRecursais(ofertaCompraId, dados);
    if (response.status !== 200 && response.status !== 201) {
      return rejectManifestacaoRequest(response);
    }
    commit(types.PRAZOS_RECURSAIS, getResponseData(response));
    return getResponseData(response);
  },
  async encerrarPrazoRecursal({ commit, rootState }, payload) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const dados = typeof payload === 'string' || payload == null ? { janela: payload } : payload;
    const response = await encerrarPrazoRecursalApi(ofertaCompraId, dados);
    if (response.status !== 200 && response.status !== 201) {
      return rejectManifestacaoRequest(response);
    }
    commit(types.PRAZOS_RECURSAIS, getResponseData(response));
    return getResponseData(response);
  },
  atualizarPrazosRecursais({ commit }, data) {
    if (data && typeof data === 'object') {
      commit(types.PRAZOS_RECURSAIS, data);
    }
  },
  async buscarPrazoManifestacaoIntencao({ commit }, ofertaCompraId) {
    try {
      const response = await dadosPrazosManifestacaoIntencao(ofertaCompraId);
      commit(types.PRAZO_MANIFESTACAO_INTENCAO, getResponseData(response));
      return getResponseData(response);
    } catch (error) {
      if (error.response?.status === 404) {
        commit(types.PRAZO_MANIFESTACAO_INTENCAO, null);
        return null;
      }
      throw error;
    }
  },
  async salvarPrazoManifestacaoIntencao({ commit, rootState }, dados) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    // Sempre POST: o relógio pode ser iniciado múltiplas vezes (janelas sucessivas).
    const response = await definirPrazosManifestacaoIntencao(ofertaCompraId, dados);
    if (response.status !== 200 && response.status !== 201) {
      return rejectManifestacaoRequest(response);
    }
    commit(types.PRAZO_MANIFESTACAO_INTENCAO, getResponseData(response));
    return getResponseData(response);
  },
  async encerrarPrazoManifestacaoIntencao({ commit, rootState }, payload = {}) {
    const ofertaCompraId = rootState.ofertasCompra.ofertaCompra.id;
    const dados = typeof payload === 'object' && payload !== null ? payload : {};
    const response = await encerrarPrazoManifestacaoIntencaoApi(ofertaCompraId, dados);
    if (response.status !== 200 && response.status !== 201) {
      return rejectManifestacaoRequest(response);
    }
    commit(types.PRAZO_MANIFESTACAO_INTENCAO, getResponseData(response));
    return getResponseData(response);
  },
  atualizarPrazoManifestacaoIntencao({ commit }, data) {
    if (data && typeof data === 'object') {
      commit(types.PRAZO_MANIFESTACAO_INTENCAO, data);
    }
  },
  atualizarRecursos({ commit, dispatch, state, rootState }, data) {
    if (!data || typeof data !== 'object') return;

    if (data.excluido && data.id) {
      commit(types.REMOVE_RECURSO, data.id);
      if (rootState.ofertasCompra?.ofertaCompra?.id) {
        dispatch('buscarElegibilidadeRazoes', rootState.ofertasCompra.ofertaCompra.id);
      }
      return;
    }

    if (data.id) {
      commit(types.UPSERT_RECURSO, data);
      if (state.prazosRecursais?.definidos && rootState.ofertasCompra?.ofertaCompra?.id) {
        dispatch('buscarPrazosRecursais', rootState.ofertasCompra.ofertaCompra.id);
      }
    }
  },
};

const getters = {
  obterOfertaCompraRecursos(state) {
    return state.recursos;
  },
  obterOfertaCompraIntencaoRecurso(state) {
    return state.intencaoRecurso;
  },
  verificarOfertaCompraIntencaoRecursoManifestada(state) {
    return state.intencaoRecurso.manifestou;
  },
  obterElegibilidadeRazoes(state) {
    return state.elegibilidadeRazoes;
  },
  dentroJanelaRazoes(state) {
    return identificarJanelaRecursalVigente(state.prazosRecursais) === 'razoes';
  },
  podeApresentarRazoes(state, localGetters) {
    return (
      state.prazosRecursais.definidos &&
      state.elegibilidadeRazoes.podeApresentarRazoes &&
      localGetters.dentroJanelaRazoes
    );
  },
  prazosRecursaisDefinidos(state) {
    return state.prazosRecursais.definidos;
  },
  prazosRecursaisHero(state, localGetters) {
    if (!state.prazosRecursais.definidos) return null;

    const blocos = {
      razoes: {
        heading: 'Data limite de razões',
        date: state.prazosRecursais.dataLimiteRazoes,
      },
      contrarrazoes: {
        heading: 'Data limite de contrarrazões',
        date: state.prazosRecursais.dataLimiteContrarrazoes,
      },
      julgamento: {
        heading: 'Data limite de julgamento',
        date: state.prazosRecursais.dataLimiteJulgamento,
      },
    };

    const janela = localGetters.janelaRecursalVigente;
    if (janela === 'razoes') return blocos.razoes;
    if (janela === 'contrarrazoes') return blocos.contrarrazoes;
    if (janela === 'julgamento') return blocos.julgamento;
    return blocos.julgamento;
  },
  janelaRecursalVigente(state) {
    return identificarJanelaRecursalVigente(state.prazosRecursais);
  },
  obterPrazosRecursais(state) {
    return state.prazosRecursais;
  },
  obterPrazoManifestacaoIntencao(state) {
    return state.prazoManifestacaoIntencao;
  },
  prazoManifestacaoIntencaoDefinido(state) {
    return Boolean(state.prazoManifestacaoIntencao?.definidos);
  },
  prazoManifestacaoIntencaoVigente(state) {
    const prazo = state.prazoManifestacaoIntencao;
    if (!prazo?.definidos) return false;
    if (Object.prototype.hasOwnProperty.call(prazo, 'vigente') && prazo.vigente !== null) {
      if (prazo.vigente) return true;
    }
    if (!prazo.dataLimite) return false;
    return !isPrazoRecursalVencido(prazo.dataLimite);
  },
};

const state = {
  recursos: [],
  intencaoRecurso: {
    manifestou: false,
    intencao: null,
  },
  elegibilidadeRazoes: {
    podeApresentarRazoes: false,
    recursoJaEnviado: false,
    recursoExcluido: false,
    intencoes: [],
  },
  prazosRecursais: {
    definidos: false,
    linhaDoTempo: null,
    dataLimiteRazoes: null,
    dataLimiteContrarrazoes: null,
    dataLimiteJulgamento: null,
    regrasAjuste: null,
  },
  prazoManifestacaoIntencao: {
    definidos: false,
    dataLimite: null,
    dataDe: null,
    vigente: false,
    tempoRestanteSegundos: null,
    limites: null,
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
};

export { types };
